'use client';

import { Suspense, useEffect, useState, useCallback, useRef } from 'react';
import Link from 'next/link';
import Script from 'next/script';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/lib/auth/context';
import type { SupportedCurrency } from '@/lib/currency';
import toast from 'react-hot-toast';
import BillingToggle from './components/BillingToggle';
import TierCard from './components/TierCard';
import FeatureComparisonTable from './components/FeatureComparisonTable';
import PricingFAQ from './components/PricingFAQ';
import DiscoveryAddonSection from './components/DiscoveryAddonSection';
import { PaymentTrustBadges } from './components/PaymentTrustBadges';
import { TIERS, FEATURE_GROUPS, FAQ_ITEMS } from './components/tierData';
import type { BillingPeriod, Tier } from './components/types';
import type { WebPlan, SubscriptionStatus } from '@/types';
import LoadingSpinner from '@/components/LoadingSpinner';
import { analyticsEvents } from '@/components/analytics/Analytics';
import { planIdFor, type PaidPlanCode } from '@/lib/billing/plans';
import { buildUpiAutopayFirstSubscriptionConfig } from '@/lib/billing/razorpay-checkout';
import { getCheckoutAttribution } from '@/lib/attribution';
import { isCurrentPaidTier, isTrialUpgradeTier } from '@/lib/billing/checkout-eligibility';
import { isTwaContext, useTwaContextResolved } from '@/lib/twa-detection';
import TwaSubscriptionNotice from '@/components/TwaSubscriptionNotice';
import { DEFAULT_LITIGANT_PRICING_OFFER } from '@/lib/litigant-pricing';
import NewPricingContent from './components/NewPricingContent';

interface RazorpayInstance {
  open: () => void;
  on: (event: string, handler: (response: { error: { description: string } }) => void) => void;
}
type PricingRazorpayConstructor = new (options: Record<string, unknown>) => RazorpayInstance;
interface RazorpaySubscriptionResponse {
  razorpay_payment_id: string;
  razorpay_subscription_id: string;
  razorpay_signature: string;
}

let razorpayScriptPromise: Promise<boolean> | null = null;

function getPricingRazorpayConstructor(): PricingRazorpayConstructor | null {
  if (typeof window === 'undefined') return null;
  return (window as Window & { Razorpay?: PricingRazorpayConstructor }).Razorpay ?? null;
}

function loadRazorpayCheckoutScript(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (getPricingRazorpayConstructor()) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;

  razorpayScriptPromise = new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      if (!ok) razorpayScriptPromise = null;
      resolve(ok);
    };

    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );
    const script = existing || document.createElement('script');

    script.addEventListener('load', () => finish(Boolean(getPricingRazorpayConstructor())), { once: true });
    script.addEventListener('error', () => finish(false), { once: true });

    if (!existing) {
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.head.appendChild(script);
    }

    window.setTimeout(() => finish(Boolean(getPricingRazorpayConstructor())), 5000);
  });

  return razorpayScriptPromise;
}

/**
 * Map DB plan tiers + billing_period to the static tier IDs used in our cards.
 * Returns the plan_id the checkout endpoint needs.
 */
function findPlanId(
  plans: WebPlan[],
  tierId: Tier['id'],
  billing: BillingPeriod,
): string | null {
  if (tierId === 'starter') {
    const starterPlanId = billing === 'yearly'
      ? planIdFor('starter', 'yearly')
      : DEFAULT_LITIGANT_PRICING_OFFER.checkoutPlanId;
    return plans.find((p) => p.id === starterPlanId)?.id || starterPlanId;
  }

  const tierMap: Partial<Record<Tier['id'], PaidPlanCode>> = {
    starter: 'starter',
    lawyer_pro: 'lawyer_pro',
    firm: 'firm',
  };
  const dbTier = tierMap[tierId];
  if (!dbTier) return null;

  const match = plans.find(
    (p) =>
      p.tier === dbTier &&
      p.billing_period === billing &&
      !p.is_trial &&
      !(p.id || '').startsWith('addon_')
  );
  if (match?.id) return match.id;
  if (tierId === 'lawyer_pro' || tierId === 'firm') {
    return planIdFor(tierId, billing);
  }
  return null;
}

function parseBillingPeriod(value: string | null): BillingPeriod | null {
  return value === 'monthly' || value === 'yearly' ? value : null;
}

function parseTierId(value: string | null): Tier['id'] | null {
  return value === 'starter' || value === 'lawyer_pro' || value === 'firm' || value === 'corporate' ? value : null;
}

const PUBLIC_TIERS = TIERS;

export default function PricingPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <PricingContent />
    </Suspense>
  );
}

function PricingContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const isTwa = useTwaContextResolved();
  const [billing, setBilling] = useState<BillingPeriod>('monthly');
  const [plans, setPlans] = useState<WebPlan[]>([]);
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
  const [activating, setActivating] = useState<string | null>(null);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [checkoutArm, setCheckoutArm] = useState<'onetime' | 'autopay' | null>(null);
  // New public pricing page (Task 2.5) — only rendered once both flags are
  // on, fetched from the public `/api/pricing-flags` endpoint below (works
  // for anonymous visitors, not just logged-in users). Defaults to
  // false/false so the fetch failing/being slow still falls through safely
  // to the unchanged legacy page below. `pricingFlagsLoaded` gates the
  // render itself (spinner until it's true) so visitors never see a flash
  // of the legacy page before the real flag values arrive.
  const [newPricingEnabled, setNewPricingEnabled] = useState(false);
  const [casePassEnabled, setCasePassEnabled] = useState(false);
  const [pricingFlagsLoaded, setPricingFlagsLoaded] = useState(false);
  const autoCheckoutKeyRef = useRef<string | null>(null);
  const currency: SupportedCurrency = 'INR';

  const resolvePlanAmount = useCallback((planId: string, billingPeriod = billing) => {
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return undefined;
    return billingPeriod === 'yearly'
      ? Number(plan.price_yearly_inr || 0)
      : Number(plan.price_monthly_inr || 0);
  }, [billing, plans]);

  useEffect(() => {
    fetchPlans();
    // New pricing page (Task 2.5) flags — this page is meant to be public
    // (specs/phase-2.md: "New public pricing page"), so the flag check must
    // work for anonymous visitors too. `/api/pricing-flags` is unauthenticated
    // on purpose.
    fetch('/api/pricing-flags')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setNewPricingEnabled(!!d.new_pricing_enabled);
        setCasePassEnabled(!!d.case_pass_enabled);
      })
      .catch(() => {})
      .finally(() => setPricingFlagsLoaded(true));
    if (isAuthenticated) {
      fetchSubscription();
      fetch('/api/subscription/checkout-arm')
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d?.arm === 'onetime' || d?.arm === 'autopay') setCheckoutArm(d.arm); })
        .catch(() => {});
    }
  }, [isAuthenticated]);

  async function fetchPlans() {
    try {
      const res = await fetch('/api/subscription/plans');
      if (res.ok) {
        const data = await res.json();
        setPlans(data.plans || []);
      }
    } catch {
      // Ignore — page still renders from hardcoded TIERS
    }
  }

  async function fetchSubscription() {
    try {
      const res = await fetch('/api/subscription');
      if (res.ok) setSubStatus(await res.json());
    } catch {
      // Ignore
    }
  }

  const currentPlanTier =
    subStatus?.subscription?.tier ||
    (subStatus?.subscription?.plan_id
      ? plans.find((p) => p.id === subStatus.subscription?.plan_id)?.tier
      : null);
  const checkoutSourceSurface = searchParams.get('source_surface') || 'pricing_page';

  // Shared verify + success handler (same as original)
  const verifyAndComplete = useCallback(
    async (
      verifyBody: Record<string, string>,
      subscriptionId: string,
      paymentAmount?: number,
      paymentCurrency?: string,
      planId?: string,
      attribution?: unknown,
    ) => {
      try {
        const verifyRes = await fetch('/api/subscription/verify-payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...verifyBody, subscription_id: subscriptionId, attribution }),
        });
        const verifyData = await verifyRes.json();
        if (verifyRes.ok && verifyData.success) {
          await analyticsEvents.subscriptionPurchased(
            paymentAmount || 1.0,
            paymentCurrency || 'INR',
            verifyBody.razorpay_payment_id,
            planId
          );
          toast.success('Plan activated! Redirecting to dashboard...');
          router.push('/dashboard');
        } else if (verifyData.internationalCardDetected) {
          toast.error(
            'International cards are not supported for this INR checkout. Please use UPI, an Indian card, or contact support.'
          );
        } else {
          toast.error(verifyData.error || 'Payment verification failed. Please contact support.');
        }
      } catch {
        toast.error(
          'Payment verification failed. If amount was deducted, it will be refunded within 5-7 days.'
        );
      } finally {
        setActivating(null);
      }
    },
    [router]
  );

  // Razorpay payment flow
  const handlePayment = useCallback(
    async (tierId: Tier['id'], billingOverride?: BillingPeriod) => {
      const billingPeriod = billingOverride || billing;

      // Firm tier collects team name + seats first on a dedicated page.
      if (tierId === 'firm') {
        router.push(`/team/new?billing=${billingPeriod}`);
        return;
      }
      if (tierId === 'corporate') {
        router.push('/request-access');
        return;
      }

      if (isCurrentPaidTier(subStatus, currentPlanTier, tierId)) {
        toast('You already have this plan active.');
        return;
      }

      const planId = findPlanId(plans, tierId, billingPeriod);
      if (!planId) {
        toast.error('Plan not available yet. Please try again later.');
        return;
      }
      const planAmount = resolvePlanAmount(planId, billingPeriod);
      const attribution = getCheckoutAttribution({
        source_surface: checkoutSourceSurface,
        plan_id: planId,
      });

      setActivating(tierId);

      try {
        const checkoutReady = razorpayLoaded || (await loadRazorpayCheckoutScript());
        const Razorpay = getPricingRazorpayConstructor();
        if (!checkoutReady || !Razorpay) {
          toast.error('Payment system could not load. Please refresh the page and try again.');
          setActivating(null);
          return;
        }

        const res = await fetch('/api/subscription/create-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan_id: planId, currency, attribution, experiment_eligible: true }),
        });
        const data = await res.json();
        if (!res.ok) {
          toast.error(data.error || 'Failed to create order');
          setActivating(null);
          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let options: any;
        if (data.checkout_mode === 'order') {
          // One-time order arm: no autopay mandate, no subscription config.
          options = {
            key: data.key_id,
            order_id: data.razorpay_order_id,
            amount: data.amount,
            currency: data.currency || 'INR',
            name: 'India Case Status',
            description: `${data.plan_name} — 30 days, one-time payment`,
            handler: async (response: { razorpay_payment_id: string; razorpay_order_id: string; razorpay_signature: string }) => {
              await verifyAndComplete(
                {
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                },
                data.subscription_id,
                planAmount,
                currency,
                planId,
                attribution,
              );
            },
            prefill: { contact: user?.phone || '', email: user?.email || '' },
            theme: { color: '#4F46E5' },
            modal: { ondismiss: () => setActivating(null) },
          };
        } else {
          const checkoutConfig = buildUpiAutopayFirstSubscriptionConfig({
            currency,
            recurringAmount: planAmount,
          });

          options = {
            key: data.key_id,
            subscription_id: data.razorpay_subscription_id,
            name: 'India Case Status',
            description: data.plan_name,
            ...(checkoutConfig ? { config: checkoutConfig } : {}),
            handler: async (response: RazorpaySubscriptionResponse) => {
              await verifyAndComplete(
                {
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_subscription_id: response.razorpay_subscription_id,
                  razorpay_signature: response.razorpay_signature,
                },
                data.subscription_id,
                planAmount,
                currency,
                planId,
                attribution,
              );
            },
            prefill: { contact: user?.phone || '', email: user?.email || '' },
            theme: { color: '#4F46E5' },
            modal: { ondismiss: () => setActivating(null) },
          };
        }

        const rzp = new Razorpay(options);
        rzp.on('payment.failed', (response: { error: { description: string } }) => {
          toast.error(`Payment failed: ${response.error.description}`);
          setActivating(null);
        });

        // Secondary Google Ads conversion: fires on checkout open (before payment).
        const plan = plans.find((p) => p.id === planId);
        if (plan) {
          const amount = planAmount;
          if (amount) analyticsEvents.subscriptionInitiated(amount, currency, planId);
        }

        rzp.open();
      } catch {
        toast.error('Something went wrong. Please try again.');
        setActivating(null);
      }
    },
    [
      razorpayLoaded,
      plans,
      billing,
      user,
      currency,
      verifyAndComplete,
      resolvePlanAmount,
      router,
      subStatus,
      currentPlanTier,
      checkoutSourceSurface,
    ]
  );

  useEffect(() => {
    if (authLoading || !isAuthenticated || searchParams.get('checkout') !== '1') return;
    // Google Play billing gate: never auto-open checkout inside the Android app.
    // Checked synchronously (not via the hook) so a deep link can't slip a
    // checkout open through before the client gate mounts.
    if (isTwaContext()) return;

    const tierId = parseTierId(searchParams.get('tier'));
    if (!tierId) return;

    const requestedBilling = parseBillingPeriod(searchParams.get('billing')) || billing;
    const checkoutKey = `${tierId}:${requestedBilling}`;
    if (autoCheckoutKeyRef.current === checkoutKey) return;
    autoCheckoutKeyRef.current = checkoutKey;

    if (requestedBilling !== billing) setBilling(requestedBilling);

    if (tierId === 'firm') {
      router.replace(`/team/new?billing=${requestedBilling}`);
      return;
    }

    if (tierId === 'corporate') {
      router.replace('/request-access');
      return;
    }

    void handlePayment(tierId, requestedBilling).finally(() => {
      router.replace('/pricing', { scroll: false });
    });
  }, [authLoading, isAuthenticated, searchParams, billing, handlePayment, router]);

  if (authLoading || !pricingFlagsLoaded) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Google Play billing gate: inside the Android app (TWA) show only a
  // text notice — no tiers, no prices, no checkout button or link. While
  // detection is unresolved (null), hold the spinner so the payment UI and
  // the Razorpay script never mount inside the TWA, not even for one frame.
  if (isTwa === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (isTwa) {
    return <TwaSubscriptionNotice heading="Plans & subscriptions" />;
  }

  // New public pricing page (Task 2.5). `case_pass` is a hard prerequisite:
  // a pricing page whose primary Case Pass CTAs do not actually
  // work is worse than showing the old page, so both flags must be on.
  // Everything below this point (the legacy JSX) is unchanged.
  if (newPricingEnabled && casePassEnabled) {
    return (
      <NewPricingContent
        subStatus={subStatus}
        isAuthenticated={isAuthenticated}
        user={user}
      />
    );
  }

  return (
    <>
      <Script
        src="https://checkout.razorpay.com/v1/checkout.js"
        strategy="afterInteractive"
        onLoad={() => setRazorpayLoaded(true)}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        {/* Hero */}
        <section className="relative overflow-hidden px-4 pt-20 pb-10 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold text-slate-900 sm:text-5xl">
              Pricing that fits your case volume
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
              Choose case tracking for your own matter, a focused solo practice, a firm workspace, or high-volume legal team.
            </p>
            <div className="mt-8 flex justify-center">
              <BillingToggle value={billing} onChange={setBilling} />
            </div>
          </div>
        </section>

        {/* Current subscription banner */}
        {subStatus?.hasActiveSubscription && (
          <div className="mx-auto max-w-7xl px-4 pb-6 sm:px-6 lg:px-8">
            <div className={`rounded-lg border p-3 text-center text-sm ${
              subStatus.isTrial
                ? 'border-indigo-200 bg-indigo-50 text-indigo-800'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}>
              {subStatus.isTrial ? (
                <>
                  Your trial is active
                  {subStatus.daysRemaining != null && ` · ${subStatus.daysRemaining} days remaining`}
                  {' · '}Subscribe now to keep alerts running after expiry.
                </>
              ) : (
                <>
                  You&apos;re on the <strong>{subStatus.subscription?.plan_name || 'Active'}</strong> plan
                  {subStatus.daysRemaining != null && ` · ${subStatus.daysRemaining} days remaining`}
                </>
              )}
            </div>
          </div>
        )}

        {/* Tier cards */}
        <section className="px-4 pb-16 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
            {PUBLIC_TIERS.map((tier) => (
              <TierCard
                key={tier.id}
                tier={tier}
                billing={billing}
                isAuthenticated={isAuthenticated}
                isCurrentTier={isCurrentPaidTier(subStatus, currentPlanTier, tier.id)}
                isTrialUpgrade={isTrialUpgradeTier(subStatus, currentPlanTier, tier.id)}
                activating={activating === tier.id}
                ctaFootnote={
                  tier.id === 'starter' && billing === 'monthly' && checkoutArm === 'onetime' && isAuthenticated
                    ? 'One-time payment · no autopay setup'
                    : undefined
                }
                onChoose={() => handlePayment(tier.id, tier.isYearlyOnly ? 'yearly' : undefined)}
              />
            ))}
          </div>

          {/* Reassurance row */}
          <div className="mx-auto mt-8 flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <svg className="h-4 w-4 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              All prices include GST
            </span>
            <span className="flex items-center gap-1.5">
              <svg className="h-4 w-4 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm4.243-10.657a1 1 0 010 1.414l-4.95 4.95a1 1 0 01-1.414 0l-2.12-2.121a1 1 0 111.414-1.414l1.414 1.414 4.243-4.243a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              UPI AutoPay-first checkout
            </span>
          </div>

          <PaymentTrustBadges />
        </section>

        {/* Case Discovery add-on */}
        <DiscoveryAddonSection />

        {/* Feature comparison */}
        <section className="px-4 pb-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <FeatureComparisonTable tiers={PUBLIC_TIERS} groups={FEATURE_GROUPS} />
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 pb-20 sm:px-6 lg:px-8">
          <PricingFAQ items={FAQ_ITEMS} />
        </section>

        {/* Footer CTA */}
        <section className="border-t border-slate-200 bg-slate-900 px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Start tracking today.
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-slate-300">
              Subscribe when you want to add active monitored cases and receive ongoing alerts.
            </p>
            <div className="mt-6">
              <Link
                href="/login?redirect=/dashboard"
                className="inline-block rounded-lg bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100"
              >
                Get started
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
